import { ClientLoggingService } from '../../../../shared/client-logging.service';
import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { HandsetDialogService } from '../../../../shared/handset-dialog.service';
import { Subject } from 'rxjs';
import { takeUntil, filter, take, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { ProjectCreationService, ProjectMetadata, UploadedFile } from '../../../../shared/project-creation.service';
import { RbacService } from '../../../../shared/rbac.service';
import { TaskService } from '../../../../shared/task.service';
import { FirebaseAuthService } from '../../../../shared/firebase-auth.service';

import { PlanRefreshService } from '../../../../shared/plan-refresh.service';
import { ConfirmDialogComponent } from '../../../../shared/confirm-dialog/confirm-dialog.component';
import { FileUploadDialogComponent, FileUploadResult } from '../file-upload-dialog/file-upload-dialog.component';
import { FilePropertiesDialogComponent } from '../file-properties-dialog/file-properties-dialog.component';
import { FileMetadataService, DocumentType, ProcessingStatus } from '../../../../shared/file-metadata.service';
import { InputFileMetadata } from '../../../../../generated.commonjs/api_pb';
import { PlanReviewerService } from '../../../../shared/plan-reviewer.service';
import {
  PageIngestionDialogComponent,
  PageIngestionDialogData,
} from '../page-ingestion-dialog/page-ingestion-dialog.component';
import { TaskCounterService } from '../../../../shared/task-counter.service';
import { LegacyProjectBannerComponent } from '../legacy-project-banner/legacy-project-banner.component';
import {
  MetadataSetupDialogComponent,
  MetadataSetupDialogData,
  MetadataSetupResult,
} from '../metadata-setup-dialog/metadata-setup-dialog.component';
import { ProjectMetadataService } from '../../../../shared/project-metadata.service';
import { GoogleMapsService, PlaceResult, AddressSuggestion } from '../../../../shared/google-maps.service';
import { ProjectLocationMapComponent } from '../project-location-map/project-location-map.component';
import { ModelConfigCardComponent } from '../model-config-card/model-config-card.component';
import { IngestionSettingsCardComponent } from '../ingestion-settings-card/ingestion-settings-card.component';
import { PowerUserModeCardComponent } from '../power-user-mode-card/power-user-mode-card.component';
import { ProgressService } from '../../../../shared/progress.service';
import { TabService } from '../../../../shared/tab.service';
import { TasksPageComponent } from '../../tasks/tasks-page/tasks-page.component';
import { CatGraphsPageComponent } from '../../cat-graphs/cat-graphs-page.component';

const US_ZIP_CODE = /^\d{5}(?:-\d{4})?$/;

/** Project settings page providing plan management (upload, delete, re-ingest pages), metadata editing, and sharing configuration. */
@Component({
  selector: 'app-project-settings',
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatListModule,
    MatDividerModule,
    MatChipsModule,
    MatToolbarModule,
    MatTableModule,
    MatSortModule,
    MatMenuModule,
    MatTooltipModule,
    MatTabsModule,
    MatSlideToggleModule,
    LegacyProjectBannerComponent,
    ProjectLocationMapComponent,
    ModelConfigCardComponent,
    IngestionSettingsCardComponent,
    PowerUserModeCardComponent,
    TasksPageComponent,
    CatGraphsPageComponent,
    MatAutocompleteModule,
  ],
  templateUrl: './project-settings.component.html',
  styleUrls: ['./project-settings.component.scss'],
})
export class ProjectSettingsComponent implements OnInit, OnDestroy, AfterViewInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private projectCreationService = inject(ProjectCreationService);
  private rbacService = inject(RbacService);
  private taskService = inject(TaskService);
  private snackBar = inject(MatSnackBar);
  private planRefreshService = inject(PlanRefreshService);
  private dialog = inject(MatDialog);
  private handsetDialog = inject(HandsetDialogService);
  private fileMetadataService = inject(FileMetadataService);
  private planReviewerService = inject(PlanReviewerService);
  private taskCounterService = inject(TaskCounterService);
  private projectMetadataService = inject(ProjectMetadataService);
  private googleMapsService = inject(GoogleMapsService);
  private authService = inject(FirebaseAuthService);
  private progressService = inject(ProgressService);
  private tabService = inject(TabService);

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('streetAddressInput') streetAddressInput!: ElementRef<HTMLInputElement>;

  projectId: string = '';
  projectMetadata: ProjectMetadata | null = null;
  uploadedFiles: UploadedFile[] = [];

  // File metadata from new gRPC service
  fileMetadataList: InputFileMetadata[] = [];
  loadingFileMetadata = false;

  // Plan data for ingested page counts
  ingestedPageCounts: Map<string, number> = new Map();
  loadingIngestedPageCounts = false;
  private ingestedPageCountRequestGeneration = 0;

  // File upload state
  selectedFile: File | null = null;
  uploading = false;

  // Project metadata state (Issue #227)
  hasProjectMetadata: boolean = false; // Whether project-metadata.json exists

  // Inline editing state (Issue #227 - Stories 1 & 2)
  // Inline editing - always show fields, track changes
  editedProjectName: string = '';
  editedDescription: string = '';
  editedAddress: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    // Google Maps metadata (Issue #236)
    googlePlaceId?: string;
    latitude?: number;
    longitude?: number;
    formattedAddress?: string;
  } = {
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'USA',
  };
  savingMetadata: boolean = false;
  hasUnsavedChanges: boolean = false;

  // Original values for change detection
  private originalProjectName: string = '';
  private originalDescription: string = '';
  private originalAddress: any = null;

  // Field-level change tracking
  nameChanged: boolean = false;
  descriptionChanged: boolean = false;
  addressChanged: boolean = false;

  // Google Maps integration (Issue #236, #1178)
  addressSuggestions: AddressSuggestion[] = [];
  private streetInput$ = new Subject<string>();
  hasValidAddress = false;
  mapCenter: { lat: number; lng: number } | null = null;
  mapZoom = 16; // Street-level zoom

  // Loading states
  loadingMetadata = false;
  loadingFiles = false;

  // Cross-reference graph state (Issue #1066)
  buildingCrossRefGraph = false;

  // Power User Mode (Issue #1228): only visible to root admin users
  isRootAdmin = false;
  showTableOfContentsWarnings = true;

  selectedTabIndex = 0;
  activeTaskCount = 0;

  // File table configuration - updated to include new metadata columns
  displayedColumns: string[] = ['name', 'size', 'pageCount', 'actions'];
  readonly ProcessingStatus = ProcessingStatus;

  private destroy$ = new Subject<void>();

  constructor() {
    ClientLoggingService.log('INFO', '🟢 ProjectSettingsComponent constructor called!');
  }

  ngOnInit(): void {
    ClientLoggingService.log('INFO', '🟢 ProjectSettingsComponent ngOnInit called!');
    ClientLoggingService.log('INFO', '🟢 ProjectSettingsComponent route snapshot:', this.route.snapshot);
    ClientLoggingService.log('INFO', '🟢 ProjectSettingsComponent route params:', this.route.snapshot.params);
    ClientLoggingService.log(
      'INFO',
      '🟢 ProjectSettingsComponent parent route params:',
      this.route.snapshot.parent?.params
    );
    this.tabService.tableOfContentsWarnings$
      .pipe(takeUntil(this.destroy$))
      .subscribe(visible => (this.showTableOfContentsWarnings = visible));

    // Get project ID from route params (MainViewComponent's route)
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.projectId = params['planId'] || '';
      ClientLoggingService.log('INFO', '🟢 ProjectSettingsComponent projectId from params:', this.projectId);
      if (this.projectId) {
        // Wait for authentication before loading data to prevent 401 errors
        this.authService.user$.pipe(take(1)).subscribe(user => {
          if (user) {
            this.loadProjectData();
          } else {
            // User not logged in yet, wait for auth to be ready
            this.authService.user$
              .pipe(
                filter(u => u !== null),
                take(1)
              )
              .subscribe(() => {
                this.loadProjectData();
              });
          }
        });
      }
    });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.syncTabIndexFromUrl();
      });

    this.syncTabIndexFromUrl();

    this.taskCounterService.activeTasks$.pipe(takeUntil(this.destroy$)).subscribe(tasks => {
      this.activeTaskCount = tasks.filter(t => t.status === 'pending' || t.status === 'processing').length;
    });

    // Issue #1228: Check if current user has root admin role for Power User Mode visibility
    this.rbacService
      .checkAdminRole('', 'root')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.isRootAdmin = response?.hasAdminRole ?? false;
        },
        error: () => {
          this.isRootAdmin = false;
        },
      });
  }

  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method
  ngAfterViewInit(): void {
    // Set up debounced street input for address suggestions (Issue #1178)
    this.setupStreetInputDebounce();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up RxJS subjects (Issue #1178)
    this.streetInput$.complete();
  }

  private async loadProjectData(): Promise<void> {
    const projectId = this.projectId;
    // Load metadata and files in parallel
    await Promise.all([this.loadProjectMetadata(), this.loadUploadedFiles(), this.loadFileMetadata()]);

    if (projectId !== this.projectId) return;
    // Load ingested page counts after file metadata is available
    await this.loadIngestedPageCounts(projectId);
  }

  private async loadProjectMetadata(): Promise<void> {
    this.loadingMetadata = true;
    try {
      const result = await this.projectCreationService.getProjectMetadata(this.projectId);
      if (result.success && result.metadata) {
        this.projectMetadata = result.metadata;
        this.hasProjectMetadata = true; // Metadata exists
        ClientLoggingService.log('INFO', '[ProjectSettings] Loaded project metadata:', this.projectMetadata);

        // Issue #1228: Sync Power User Mode tab visibility
        this.tabService.setPowerUserMode(this.projectMetadata.power_user_mode ?? false);

        // Populate form fields (inline editing - always visible)
        this.loadMetadataIntoForm();

        // Update map display after metadata loads
        this.updateMapLocation();

        // Issue #236 / #1683: Auto-geocode if address exists but no coordinates (non-blocking)
        this.autoGeocodeIfNeeded()
          .then(() => {
            const address = this.projectMetadata?.address;
            if (address && address.latitude && address.longitude && !this.addressChanged) {
              this.editedAddress.latitude = address.latitude;
              this.editedAddress.longitude = address.longitude;
              this.editedAddress.googlePlaceId = address.googlePlaceId;
              this.editedAddress.formattedAddress = address.formattedAddress;
              this.originalAddress = { ...this.editedAddress };
            }
            this.updateMapLocation();
          })
          .catch(err => {
            ClientLoggingService.log('WARN', '[ProjectSettings] Auto-geocode failed (non-critical):', err);
          });
      } else {
        // No metadata found - show legacy project banner
        this.hasProjectMetadata = false;
        ClientLoggingService.log('INFO', '[ProjectSettings] No project metadata found - showing legacy banner');
      }
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectSettings] Error loading project metadata:', error);
      this.hasProjectMetadata = false;
      // Don't show error snackbar for missing metadata (it's expected for legacy projects)
    } finally {
      this.loadingMetadata = false;
    }
  }

  private async loadUploadedFiles(): Promise<void> {
    this.loadingFiles = true;
    try {
      const result = await this.projectCreationService.getUploadedFiles(this.projectId);
      if (result.success && result.files) {
        this.uploadedFiles = result.files;

        // Files are now available for ingestion via row clicks

        ClientLoggingService.log('INFO', '[ProjectSettings] Loaded uploaded files:', this.uploadedFiles);
      } else {
        this.snackBar.open(result.error || 'Failed to load uploaded files', 'Close', { duration: 3000 });
      }
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectSettings] Error loading uploaded files:', error);
      this.snackBar.open('Failed to load uploaded files', 'Close', { duration: 3000 });
    } finally {
      this.loadingFiles = false;
    }
  }

  private async loadFileMetadata(): Promise<void> {
    this.loadingFileMetadata = true;
    try {
      ClientLoggingService.log('INFO', '[ProjectSettings] Loading file metadata for project:', this.projectId);

      // Use the new file metadata service to get comprehensive file information
      return new Promise<void>((resolve, reject) => {
        this.fileMetadataService
          .listFileMetadata(this.projectId, 100) // Load up to 100 files
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: response => {
              this.fileMetadataList = response.getFilesList();
              ClientLoggingService.log(
                'INFO',
                '[ProjectSettings] Loaded file metadata:',
                this.fileMetadataList.length,
                'files'
              );

              // Log some details for debugging
              this.fileMetadataList.forEach(file => {
                ClientLoggingService.log(
                  'INFO',
                  `[ProjectSettings] File: ${file.getFileName()}, Type: ${this.fileMetadataService.formatDocumentType(file.getDocumentType())}, Status: ${this.fileMetadataService.formatProcessingStatus(file.getProcessingStatus())}`
                );
              });
            },
            error: error => {
              ClientLoggingService.log('ERROR', '[ProjectSettings] Error loading file metadata:', error);
              this.snackBar.open('Failed to load file metadata', 'Close', { duration: 3000 });
              this.loadingFileMetadata = false;
              reject(error);
            },
            complete: () => {
              this.loadingFileMetadata = false;
              resolve();
            },
          });
      });
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectSettings] Error loading file metadata:', error);
      this.snackBar.open('Failed to load file metadata', 'Close', { duration: 3000 });
      this.loadingFileMetadata = false;
      throw error;
    }
  }

  private async loadIngestedPageCounts(projectId = this.projectId): Promise<void> {
    const requestGeneration = ++this.ingestedPageCountRequestGeneration;
    const fileMetadata = [...this.fileMetadataList];
    this.loadingIngestedPageCounts = true;
    this.ingestedPageCounts.clear();
    try {
      ClientLoggingService.log('INFO', '[ProjectSettings] Loading ingested page counts for project:', projectId);

      // Get the architectural plan which contains all ingested pages
      const plan = await this.planReviewerService.getArchitecturalPlan(projectId);
      if (requestGeneration !== this.ingestedPageCountRequestGeneration || projectId !== this.projectId) return;
      const planObject = plan.toObject();

      if (planObject && planObject.pagesList) {
        ClientLoggingService.log(
          'INFO',
          '[ProjectSettings] Found',
          planObject.pagesList.length,
          'ingested pages total'
        );

        // NEW (Issue #167): Count pages by file using extractedPagesList from file metadata
        // Each file's metadata has extractedPagesList showing which pages belong to it
        const counts = new Map<string, number>();
        for (const file of fileMetadata) {
          const extractedPages = file.getExtractedPagesList();
          const ingestedCount = extractedPages.length;

          counts.set(file.getFileId(), ingestedCount);
          ClientLoggingService.log(
            'INFO',
            `[ProjectSettings] File ${file.getFileId()} (${file.getFileName()}): ${ingestedCount} pages ingested`
          );
        }
        this.ingestedPageCounts = counts;
      }
    } catch (error) {
      if (requestGeneration === this.ingestedPageCountRequestGeneration && projectId === this.projectId) {
        ClientLoggingService.log('ERROR', '[ProjectSettings] Error loading ingested page counts:', error);
      }
      // Don't show error to user as this is supplementary data
    } finally {
      if (requestGeneration === this.ingestedPageCountRequestGeneration && projectId === this.projectId) {
        this.loadingIngestedPageCounts = false;
      }
    }
  }

  getIngestedPageCountState(file: InputFileMetadata | null | undefined): 'loading' | 'known' | 'unavailable' {
    if (!file) return 'unavailable';
    if (this.loadingIngestedPageCounts) return 'loading';
    return this.ingestedPageCounts.has(file.getFileId()) ? 'known' : 'unavailable';
  }

  getIngestedPageCountOrNull(file: InputFileMetadata | null | undefined): number | null {
    return this.getIngestedPageCountState(file) === 'known' ? this.getIngestedPageCount(file) : null;
  }

  // Get ingested page count for a specific file
  getIngestedPageCount(file: InputFileMetadata | null | undefined): number {
    if (!file) return 0;
    return this.ingestedPageCounts.get(file.getFileId()) ?? 0;
  }

  // File row click handler
  onFileRowClick(file: InputFileMetadata): void {
    if (file.getProcessingStatus() === ProcessingStatus.PROCESSING_STATUS_FAILED) {
      this.snackBar.open(file.getMetadataError() || 'Retry metadata before processing this file.', 'Close', {
        duration: 5000,
      });
      return;
    }
    ClientLoggingService.log('INFO', '[ProjectSettings] File clicked for ingestion:', file.getFileName());

    const dialogData: PageIngestionDialogData = {
      file: file,
      projectId: this.projectId,
      ingestedPageCount: this.getIngestedPageCount(file),
    };

    const dialogRef = this.handsetDialog.open(PageIngestionDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      data: dialogData,
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.action === 'ingest' && result.started) {
        ClientLoggingService.log('INFO', '[ProjectSettings] Page ingestion started:', result);

        // The dialog has already shown the snackbar and started tasks
        // We don't need to do anything else here since task tracking
        // is now handled by the TaskCounterService

        // Optionally reload project data to refresh the table
        // (though this might not be necessary since no pages are ingested yet)
        // this.loadProjectData();
      }
    });
  }

  // File upload methods
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      ClientLoggingService.log('INFO', '[ProjectSettings] File selected:', this.selectedFile.name);
    }
  }

  /**
   * Opens the file properties dialog to display detailed metadata.
   * Issue #167: Shows InputFileMetadata fields for debugging and info.
   */
  openFilePropertiesDialog(file: any): void {
    ClientLoggingService.log('INFO', '[ProjectSettings] Opening properties dialog for file:', file.toObject());

    const dialogRef = this.dialog.open(FilePropertiesDialogComponent, {
      width: '700px',
      data: { fileMetadata: file.toObject() },
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe(result => {
      ClientLoggingService.log(
        'INFO',
        '[ProjectSettings] Properties dialog closed with result:',
        result ?? 'cancelled'
      );
    });
  }

  /**
   * Placeholder for file download (to be implemented).
   */
  downloadFile(file: any): void {
    ClientLoggingService.log('INFO', '[ProjectSettings] Download file:', file.getFileId());
    // TODO: Implement file download
  }

  /**
   * Deletes a specific input file and its associated metadata after user confirmation.
   * Issue #167: Implements file deletion.
   */
  deleteFile(file: InputFileMetadata): void {
    const fileName = file.getFileName();
    const fileId = file.getFileId();

    ClientLoggingService.log('INFO', '[ProjectSettings] Confirming deletion for file:', fileName);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete File',
        message: `Are you sure you want to permanently delete "${fileName}"? This will also remove any ingested pages from this file.`,
        confirmText: 'Delete',
        confirmColor: 'warn',
      },
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        ClientLoggingService.log('INFO', '[ProjectSettings] Deleting file:', fileId);
        this.progressService.setProgressState({
          isLoading: true,
          message: `Deleting ${fileName}...`,
          isColdStart: false,
          progress: 50,
        });

        this.fileMetadataService.deleteInputFile(this.projectId, fileId).subscribe({
          next: async () => {
            this.snackBar.open('File deleted successfully', 'Close', { duration: 3000 });
            this.progressService.hideProgress();
            // Refresh file lists properly awaiting them
            await Promise.all([this.loadUploadedFiles(), this.loadFileMetadata()]);
            // Also refresh plan to update page counts/list
            this.planRefreshService.refreshPlan(this.projectId, 'File deleted');
          },
          error: error => {
            ClientLoggingService.log('ERROR', '[ProjectSettings] Failed to delete file:', error);
            this.snackBar.open('Failed to delete file: ' + (error?.message || 'Unknown error'), 'Close', {
              duration: 5000,
            });
            this.progressService.hideProgress();
          },
        });
      }
    });
  }

  openUploadDialog(): void {
    const dialogRef = this.handsetDialog.open(FileUploadDialogComponent, {
      width: '500px',
      data: { projectId: this.projectId },
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe(async (result: FileUploadResult) => {
      if (result && result.success && result.file) {
        this.uploading = true;
        // Start progress tracking for file upload
        this.progressService.setProgressState({
          isLoading: true,
          message: `Preparing to upload ${result.file.name}...`,
          isColdStart: false,
          progress: 5,
        });
        try {
          ClientLoggingService.log('INFO', '[ProjectSettings] Uploading file from dialog:', result.file.name);

          const uploadResult = await this.projectCreationService.uploadFileToProject(this.projectId, result.file);

          if (uploadResult.success) {
            this.progressService.completeProgress('Upload complete!');
            this.snackBar.open(uploadResult.message || 'File uploaded successfully!', 'Close', { duration: 4000 });
            await Promise.all([this.loadUploadedFiles(), this.loadFileMetadata()]);
          } else if (uploadResult.partialUpload) {
            this.progressService.completeProgress('Upload needs attention');
            await Promise.all([this.loadUploadedFiles(), this.loadFileMetadata()]);
            this.snackBar.open(uploadResult.error || 'File uploaded, but metadata registration failed.', 'Close', {
              duration: 7000,
            });
          } else {
            this.progressService.hideProgress();
            this.snackBar.open(uploadResult.error || 'Failed to upload file', 'Close', { duration: 5000 });
          }
        } catch (error) {
          ClientLoggingService.log('ERROR', '[ProjectSettings] Error uploading file:', error);
          this.progressService.hideProgress();
          this.snackBar.open('Error uploading file', 'Close', { duration: 3000 });
        } finally {
          this.uploading = false;
        }
      }
    });
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  clearSelectedFile(): void {
    this.selectedFile = null;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  async uploadFile(): Promise<void> {
    if (!this.selectedFile) {
      this.snackBar.open('Please select a file to upload', 'Close', { duration: 3000 });
      return;
    }

    this.uploading = true;
    try {
      ClientLoggingService.log('INFO', '[ProjectSettings] Uploading file:', this.selectedFile.name);

      // Use the existing project creation service for file upload
      const result = await this.projectCreationService.uploadFileToProject(this.projectId, this.selectedFile);

      if (result.success) {
        this.snackBar.open(result.message || 'File uploaded successfully!', 'Close', { duration: 4000 });
        this.clearSelectedFile();
        await Promise.all([this.loadUploadedFiles(), this.loadFileMetadata()]);
      } else if (result.partialUpload) {
        this.clearSelectedFile();
        await Promise.all([this.loadUploadedFiles(), this.loadFileMetadata()]);
        this.snackBar.open(result.error || 'File uploaded, but metadata registration failed.', 'Close', {
          duration: 7000,
        });
      } else {
        this.snackBar.open(result.error || 'Failed to upload file', 'Close', { duration: 5000 });
      }
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectSettings] Error uploading file:', error);
      this.snackBar.open('Error uploading file', 'Close', { duration: 3000 });
    } finally {
      this.uploading = false;
    }
  }

  // Utility methods
  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleString();
  }

  // File metadata utility methods
  formatFileSize(bytes: number): string {
    return this.fileMetadataService.formatFileSize(bytes);
  }

  formatDocumentType(documentType: DocumentType): string {
    return this.fileMetadataService.formatDocumentType(documentType);
  }

  formatProcessingStatus(status: ProcessingStatus): string {
    return this.fileMetadataService.formatProcessingStatus(status);
  }

  getStatusColorClass(status: ProcessingStatus): string {
    return this.fileMetadataService.getStatusColorClass(status);
  }

  retryFileMetadata(file: InputFileMetadata): void {
    this.progressService.setProgressState({
      isLoading: true,
      message: `Retrying metadata for ${file.getFileName()}...`,
      isColdStart: false,
      progress: 50,
    });
    this.fileMetadataService.generateFileMetadata(this.projectId, file.getFileName(), true).subscribe({
      next: async () => {
        await this.loadFileMetadata();
        this.progressService.completeProgress('Metadata generated');
        this.snackBar.open('Metadata generated successfully', 'Close', { duration: 3000 });
      },
      error: error => {
        ClientLoggingService.log('ERROR', '[ProjectSettings] Metadata retry failed:', error);
        this.progressService.hideProgress();
        this.snackBar.open('Metadata generation failed. The file remains available for retry or deletion.', 'Close', {
          duration: 6000,
        });
      },
    });
  }

  isUploadValid(): boolean {
    return !!this.selectedFile && !this.uploading;
  }

  // ========== PROJECT METADATA MANAGEMENT (Issue #227) ==========

  /**
   * Opens dialog to set up metadata for legacy project (Story 3).
   */
  openMetadataSetupDialog(): void {
    ClientLoggingService.log('INFO', '[ProjectSettings] Opening metadata setup dialog for project:', this.projectId);

    const dialogRef = this.handsetDialog.open(MetadataSetupDialogComponent, {
      width: '600px',
      disableClose: false,
      data: {
        projectId: this.projectId,
        defaultProjectName: this.projectId, // Pre-fill with project ID
      } as MetadataSetupDialogData,
    });

    dialogRef.afterClosed().subscribe(async (result: MetadataSetupResult) => {
      if (result && result.success) {
        await this.createProjectMetadata(result.projectName!, result.projectDescription!);
      }
    });
  }

  /**
   * Creates project metadata via gRPC (Story 3).
   */
  private async createProjectMetadata(projectName: string, projectDescription: string): Promise<void> {
    ClientLoggingService.log('INFO', '[ProjectSettings] Creating project metadata:', {
      projectName,
      projectDescription,
    });

    try {
      // Call CreateProjectMetadata gRPC
      const response = await this.projectMetadataService.createProjectMetadata(
        this.projectId,
        projectName,
        projectDescription
      );

      ClientLoggingService.log('INFO', '[ProjectSettings] Metadata created successfully:', response.toObject());
      this.snackBar.open('Project metadata created successfully!', 'Close', { duration: 3000 });

      // Update local state
      this.hasProjectMetadata = true;

      // Reload metadata to update UI with server-populated fields
      await this.loadProjectMetadata();
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectSettings] Error creating metadata:', error);
      this.snackBar.open('Failed to create project metadata: ' + error, 'Close', { duration: 5000 });
    }
  }

  /**
   * Handles banner dismissal (temporary, reappears on reload).
   */
  onBannerDismiss(): void {
    ClientLoggingService.log('INFO', '[ProjectSettings] Legacy banner dismissed temporarily');
    // Banner will reappear on page reload since hasProjectMetadata is still false
  }

  // ========== INLINE EDITING (Issue #227 - Stories 1 & 2) ==========

  /**
   * Loads metadata into form fields (inline editing - always visible).
   */
  private loadMetadataIntoForm(): void {
    if (!this.projectMetadata) return;

    // Populate form fields
    this.editedProjectName = this.projectMetadata.project_name;
    this.editedDescription = this.projectMetadata.description || '';

    // Store original values for change detection
    this.originalProjectName = this.projectMetadata.project_name;
    this.originalDescription = this.projectMetadata.description || '';

    // Load address
    const projectAddress = this.projectMetadata.address;
    if (projectAddress) {
      this.editedAddress = {
        street: projectAddress.street || '',
        city: projectAddress.city || '',
        state: projectAddress.state || '',
        postalCode: projectAddress.postalCode || '',
        country: projectAddress.country || 'USA',
        googlePlaceId: projectAddress.googlePlaceId || undefined,
        latitude: projectAddress.latitude || undefined,
        longitude: projectAddress.longitude || undefined,
        formattedAddress: projectAddress.formattedAddress || undefined,
      };
      this.originalAddress = { ...this.editedAddress };
    } else {
      this.editedAddress = {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'USA',
      };
      this.originalAddress = { ...this.editedAddress };
    }

    this.hasUnsavedChanges = false;
    this.nameChanged = false;
    this.descriptionChanged = false;
    this.addressChanged = false;

    // Preload Google Maps API so autocomplete is ready when user types (Issue #1178)
    this.googleMapsService.loadGoogleMaps().catch(err => {
      ClientLoggingService.log('WARN', '[ProjectSettings] Maps API preload failed:', err);
    });
  }

  /**
   * Checks if any fields have been modified.
   */
  private checkForChanges(): void {
    this.nameChanged = this.editedProjectName !== this.originalProjectName;
    this.descriptionChanged = this.editedDescription !== this.originalDescription;
    this.addressChanged = JSON.stringify(this.editedAddress) !== JSON.stringify(this.originalAddress);

    this.hasUnsavedChanges = this.nameChanged || this.descriptionChanged || this.addressChanged;
  }

  /**
   * Called when any field changes.
   */
  onFieldChange(): void {
    this.checkForChanges();
  }

  /** Returns true when a nonempty US ZIP code is not five digits or ZIP+4. */
  isPostalCodeInvalid(): boolean {
    const postalCode = this.editedAddress?.postalCode ?? '';
    return postalCode.length > 0 && !US_ZIP_CODE.test(postalCode);
  }

  /**
   * Discards unsaved changes and reverts to original values.
   */
  discardChanges(): void {
    this.loadMetadataIntoForm();
    this.hasUnsavedChanges = false;

    // Revert map to saved state (Issue #236)
    this.updateMapLocation();
  }

  /**
   * Saves all edited metadata fields (name, description, address).
   */
  async saveMetadata(): Promise<void> {
    if (!this.projectMetadata || this.savingMetadata) return;

    const trimmedName = this.editedProjectName.trim();
    if (!trimmedName) {
      this.snackBar.open('Project name cannot be empty', 'Close', { duration: 3000 });
      return;
    }
    if (this.isPostalCodeInvalid()) {
      this.snackBar.open('Enter a valid ZIP code', 'Close', { duration: 3000 });
      return;
    }

    this.savingMetadata = true;
    try {
      // Geocode address if not already geocoded (Issue #236)
      if (!this.editedAddress.latitude && this.isAddressComplete()) {
        ClientLoggingService.log('INFO', '[ProjectSettings] Geocoding address before save...');
        await this.geocodeAddress();
      }

      // Prepare address object with Google Maps metadata (Issue #236)
      const address = {
        street: this.editedAddress.street.trim(),
        city: this.editedAddress.city.trim(),
        state: this.editedAddress.state.trim(),
        postalCode: this.editedAddress.postalCode.trim(),
        country: this.editedAddress.country.trim(),
        googlePlaceId: this.editedAddress.googlePlaceId || '',
        latitude: this.editedAddress.latitude || 0,
        longitude: this.editedAddress.longitude || 0,
        formattedAddress: this.editedAddress.formattedAddress || '',
      };

      // Update name, description, and address in one call
      const response = await this.projectMetadataService.updateProjectMetadata(this.projectId, {
        projectName: trimmedName,
        projectDescription: this.editedDescription.trim(),
        projectAddress: address,
      });

      ClientLoggingService.log('INFO', '[ProjectSettings] Update response:', response.toObject());

      this.snackBar.open('Project metadata updated successfully', 'Close', { duration: 3000 });

      // Use the response from UpdateProjectMetadata to update local metadata
      const updatedMetadata = response.getMetadata();
      if (updatedMetadata && this.projectMetadata) {
        this.projectMetadata.project_name = updatedMetadata.getProjectName();
        this.projectMetadata.description = updatedMetadata.getProjectDescription();

        // Store address from response (including Google Maps metadata - Issue #236)
        const responseAddress = updatedMetadata.getProjectAddress();
        if (responseAddress) {
          this.projectMetadata.address = {
            street: responseAddress.getStreet(),
            city: responseAddress.getCity(),
            state: responseAddress.getState(),
            postalCode: responseAddress.getPostalCode(),
            country: responseAddress.getCountry(),
            googlePlaceId: responseAddress.getGooglePlaceId(),
            latitude: responseAddress.getLatitude(),
            longitude: responseAddress.getLongitude(),
            formattedAddress: responseAddress.getFormattedAddress(),
          };
        }
      }

      // Reload form to update original values (resets hasUnsavedChanges)
      this.loadMetadataIntoForm();

      // Update map display (Issue #236)
      this.updateMapLocation();
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectSettings] Error updating metadata:', error);
      this.snackBar.open('Failed to update project metadata', 'Close', { duration: 5000 });
    } finally {
      this.savingMetadata = false;
    }
  }

  /**
   * Gets project address from metadata (handles old schema).
   */
  getProjectAddress(): any {
    return this.projectMetadata?.address;
  }

  /**
   * Checks if project has address data.
   */
  hasAddress(): boolean {
    const projectAddress = this.getProjectAddress();
    return projectAddress && (projectAddress.street || projectAddress.city || projectAddress.state);
  }

  /**
   * Formats address for display.
   */
  formatAddress(address: any): string {
    if (!address) return '';

    const parts: string[] = [];
    if (address.street) parts.push(address.street);

    const cityStateZip: string[] = [];
    if (address.city) cityStateZip.push(address.city);
    if (address.state) cityStateZip.push(address.state);
    if (address.postalCode) cityStateZip.push(address.postalCode);

    if (cityStateZip.length > 0) {
      parts.push(cityStateZip.join(', '));
    }

    if (address.country && address.country !== 'USA') {
      parts.push(address.country);
    }

    return parts.join('\n');
  }

  /**
   * Sets up debounced street input to fetch autocomplete suggestions.
   * Called once from ngAfterViewInit. Uses RxJS to debounce keystrokes.
   */
  private setupStreetInputDebounce(): void {
    this.streetInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(async (input: string) => {
          const countryCode = this.editedAddress.country?.toLowerCase() || 'us';
          return this.googleMapsService.fetchAddressSuggestions(input, countryCode);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(suggestions => {
        this.addressSuggestions = suggestions;
      });
  }

  /**
   * Called on every keystroke in the street input.
   * Feeds the input to the debounced Subject for suggestion fetching.
   */
  onStreetInputChange(value: string | AddressSuggestion): void {
    this.onFieldChange();
    // After mat-autocomplete selection, ngModel may hold an AddressSuggestion object
    const text = typeof value === 'string' ? value : value?.text || '';
    this.streetInput$.next(text);
  }

  /**
   * Called when user selects a suggestion from mat-autocomplete.
   * Fetches full place details and populates all address fields.
   */
  async onAddressSuggestionSelected(event: MatAutocompleteSelectedEvent): Promise<void> {
    const suggestion: AddressSuggestion = event.option.value;
    if (!suggestion?.placePrediction) {
      return;
    }

    try {
      const place = await this.googleMapsService.getPlaceDetails(suggestion.placePrediction);
      if (place) {
        this.handlePlaceSelected(place);
      }
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectSettings] Failed to get place details:', error);
    }
  }

  /**
   * Display function for mat-autocomplete options.
   * Shows the suggestion text, or returns the raw string for manual input.
   */
  displaySuggestion(suggestion: AddressSuggestion | string): string {
    if (typeof suggestion === 'string') {
      return suggestion;
    }
    return suggestion?.text || '';
  }

  /**
   * Handles Google Places Autocomplete selection.
   * Parses the selected place and populates address fields.
   */
  private handlePlaceSelected(place: PlaceResult): void {
    // Validate place data
    if (!place || !place.address_components) {
      ClientLoggingService.log('WARN', '[ProjectSettings] Invalid place selected:', place);
      return;
    }

    ClientLoggingService.log('INFO', '[ProjectSettings] Place selected:', place);

    // Parse address components
    let streetNumber = '';
    let route = '';
    let city = '';
    let state = '';
    let postalCode = '';
    let country = '';

    for (const component of place.address_components) {
      const type = component.types[0];

      switch (type) {
        case 'street_number':
          streetNumber = component.long_name;
          break;
        case 'route':
          route = component.long_name;
          break;
        case 'locality':
          city = component.long_name;
          break;
        case 'administrative_area_level_1':
          state = component.short_name; // Use short name for states (e.g., "CA")
          break;
        case 'postal_code':
          postalCode = component.long_name;
          break;
        case 'country':
          country = component.short_name; // Use short name (e.g., "US")
          break;
      }
    }

    // Update form fields
    this.editedAddress.street = `${streetNumber} ${route}`.trim();
    this.editedAddress.city = city;
    this.editedAddress.state = state;
    this.editedAddress.postalCode = postalCode;
    this.editedAddress.country = country || 'USA';

    // Store Google Maps metadata
    if (place.geometry?.location) {
      const loc = place.geometry.location;
      // Handle both LatLng objects (with .lat() method) and LatLngLiteral ({lat, lng})
      this.editedAddress.latitude = typeof loc.lat === 'function' ? loc.lat() : (loc.lat as unknown as number);
      this.editedAddress.longitude = typeof loc.lng === 'function' ? loc.lng() : (loc.lng as unknown as number);
      ClientLoggingService.log(
        'INFO',
        '[ProjectSettings] Geocoded location:',
        this.editedAddress.latitude,
        this.editedAddress.longitude
      );

      // Update map preview immediately (Issue #236)
      this.mapCenter = {
        lat: this.editedAddress.latitude,
        lng: this.editedAddress.longitude,
      };
      this.hasValidAddress = true;
    }

    if (place.place_id) {
      this.editedAddress.googlePlaceId = place.place_id;
      ClientLoggingService.log('INFO', '[ProjectSettings] Google Place ID:', place.place_id);
    }

    if (place.formatted_address) {
      this.editedAddress.formattedAddress = place.formatted_address;
      ClientLoggingService.log('INFO', '[ProjectSettings] Formatted address:', place.formatted_address);
    }

    // Trigger change detection to show Save/Discard buttons
    this.checkForChanges();

    // Show success feedback
    this.snackBar.open('Address populated from Google Places', 'Close', { duration: 2000 });
  }

  /**
   * Checks if all required address fields are filled.
   */
  private isAddressComplete(): boolean {
    return !!(
      this.editedAddress.street &&
      this.editedAddress.city &&
      this.editedAddress.state &&
      this.editedAddress.postalCode
    );
  }

  /**
   * Geocodes the current edited address.
   * Updates latitude, longitude, and formatted address.
   */
  private async geocodeAddress(): Promise<void> {
    const fullAddress = this.formatAddressForGeocoding();

    try {
      const result = await this.googleMapsService.geocodeAddress(fullAddress);

      if (result && result.geometry?.location) {
        this.editedAddress.latitude = result.geometry.location.lat();
        this.editedAddress.longitude = result.geometry.location.lng();
        this.editedAddress.formattedAddress = result.formatted_address;

        // Also store place_id if available
        if (result.place_id) {
          this.editedAddress.googlePlaceId = result.place_id;
        }

        ClientLoggingService.log('INFO', '[ProjectSettings] Geocoding successful:', result);
      } else {
        ClientLoggingService.log('WARN', '[ProjectSettings] Geocoding returned no results');
        // Non-blocking - proceed with save even if geocoding fails
      }
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectSettings] Geocoding error:', error);
      // Non-blocking - proceed with save even if geocoding fails
    }
  }

  /**
   * Formats the address for geocoding API call.
   */
  private formatAddressForGeocoding(): string {
    return [
      this.editedAddress.street,
      this.editedAddress.city,
      this.editedAddress.state,
      this.editedAddress.postalCode,
      this.editedAddress.country,
    ]
      .filter(Boolean)
      .join(', ');
  }

  /**
   * Updates the map center and visibility based on current address.
   * Called after metadata load and after address save.
   */
  private updateMapLocation(): void {
    const address = this.getProjectAddress();

    if (address?.latitude && address?.longitude) {
      this.hasValidAddress = true;
      this.mapCenter = {
        lat: address.latitude,
        lng: address.longitude,
      };
      ClientLoggingService.log('INFO', '[ProjectSettings] Map center updated:', this.mapCenter);
    } else {
      this.hasValidAddress = false;
      this.mapCenter = null;
      ClientLoggingService.log('INFO', '[ProjectSettings] No valid address for map display');
    }
  }

  /**
   * Auto-geocodes address if it exists but has no coordinates.
   * Called after metadata load to backfill legacy addresses.
   * Issue #236: Requirement 4.3 - Auto-geocode on load
   */
  private async autoGeocodeIfNeeded(): Promise<void> {
    if (!this.projectMetadata?.address) {
      return; // No address at all
    }

    const address = this.projectMetadata.address;

    // Check if address has basic fields but missing coordinates
    const hasBasicAddress = !!(address.street && address.city && address.state);
    const hasCoordinates = !!(address.latitude && address.longitude);

    if (hasBasicAddress && !hasCoordinates) {
      ClientLoggingService.log('INFO', '[ProjectSettings] Address exists but no coordinates - auto-geocoding...');

      // Build address string
      const fullAddress = [address.street, address.city, address.state, address.postalCode, address.country]
        .filter(Boolean)
        .join(', ');

      try {
        const result = await this.googleMapsService.geocodeAddress(fullAddress);

        if (result && result.geometry?.location) {
          // Backfill the coordinates
          const latitude = result.geometry.location.lat();
          const longitude = result.geometry.location.lng();
          const googlePlaceId = result.place_id || '';
          const formattedAddress = result.formatted_address;

          ClientLoggingService.log('INFO', '[ProjectSettings] Auto-geocoding successful:', { latitude, longitude });

          // Save to backend automatically
          const response = await this.projectMetadataService.updateProjectMetadata(this.projectId, {
            projectAddress: {
              ...address,
              googlePlaceId,
              latitude,
              longitude,
              formattedAddress,
            },
          });

          // Update local metadata
          const updatedMetadata = response.getMetadata();
          if (updatedMetadata && this.projectMetadata) {
            const responseAddress = updatedMetadata.getProjectAddress();
            if (responseAddress) {
              this.projectMetadata.address = {
                street: responseAddress.getStreet(),
                city: responseAddress.getCity(),
                state: responseAddress.getState(),
                postalCode: responseAddress.getPostalCode(),
                country: responseAddress.getCountry(),
                googlePlaceId: responseAddress.getGooglePlaceId(),
                latitude: responseAddress.getLatitude(),
                longitude: responseAddress.getLongitude(),
                formattedAddress: responseAddress.getFormattedAddress(),
              };
            }
          }

          ClientLoggingService.log('INFO', '[ProjectSettings] Auto-geocoded address backfilled successfully');
        }
      } catch (error) {
        ClientLoggingService.log('WARN', '[ProjectSettings] Auto-geocoding failed (non-critical):', error);
        // Non-critical - user can still manually geocode via edit
      }
    }
  }

  /** Triggers cross-reference graph construction for the project (Issue #1066). */
  async buildCrossReferenceGraph(): Promise<void> {
    if (!this.projectId || this.buildingCrossRefGraph) {
      return;
    }

    this.buildingCrossRefGraph = true;
    try {
      const result = await this.planReviewerService.buildCrossReferenceGraph(this.projectId);
      if (result.success) {
        this.taskCounterService.addTask(result.taskId, this.projectId, 'cross-reference-graph');
        this.snackBar.open('Cross-reference graph build started', 'OK', { duration: 4000 });
      } else {
        this.snackBar.open(`Failed: ${result.message}`, 'Dismiss', { duration: 6000 });
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      this.snackBar.open(`Error: ${msg}`, 'Dismiss', { duration: 6000 });
    } finally {
      this.buildingCrossRefGraph = false;
    }
  }

  onTabChange(event: any): void {
    if (!this.projectId) return;

    const tabIndex = event.index;
    if (tabIndex === 2) {
      this.router.navigate(['/projects', this.projectId, 'tasks']);
    } else if (tabIndex === 3) {
      this.router.navigate(['/projects', this.projectId, 'settings', 'settings']);
    } else if (tabIndex === 4) {
      this.router.navigate(['/projects', this.projectId, 'cat-graphs']);
    } else {
      const tabPath = tabIndex === 0 ? 'overview' : 'files';
      this.router.navigate(['/projects', this.projectId, 'settings', tabPath]);
    }
  }

  private syncTabIndexFromUrl(): void {
    const url = this.router.url;
    if (url.includes('/cat-graphs')) {
      this.selectedTabIndex = 4;
    } else if (url.includes('/settings/settings') || url.includes('/settings/config')) {
      this.selectedTabIndex = 3;
    } else if (url.includes('/tasks')) {
      this.selectedTabIndex = 2;
    } else if (url.includes('/settings/files')) {
      this.selectedTabIndex = 1;
    } else {
      this.selectedTabIndex = 0;
    }
  }

  onTableOfContentsWarningsChanged(visible: boolean): void {
    this.tabService.saveTableOfContentsWarnings(this.projectId, visible);
  }

  onPowerUserModeChanged(enabled: boolean): void {
    if (this.projectMetadata) {
      this.projectMetadata.power_user_mode = enabled;
    }
    this.tabService.setPowerUserMode(enabled);
  }
}
